import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ApiResultModel as OrderStatusApiResultModel } from "@WhiteCap/Store/Service/CustomOrderStatusPageService";
import { PickupWindowApiResultModel } from "@WhiteCap/Store/Service/PickupWindowService";
import { Draft } from "immer";

export interface OrderStatusModel {
    isLoading: boolean;
    error: string;
    orderStatusById: {
        [id: string]: OrderStatusApiResultModel;
    };
    pickupWindow: {
        isUpdatingSchedule: boolean;
        isLoading: boolean;
        error: string;
        pickUpWindowByOrderId: {
            [id: string]: PickupWindowApiResultModel;
        };
    };
}

const initialState: OrderStatusModel = {
    isLoading: false,
    error: "",
    orderStatusById: {},
    pickupWindow: {
        isLoading: false,
        isUpdatingSchedule: false,
        error: "",
        pickUpWindowByOrderId: {},
    },
};

const convertToDateString = (dateString?: string): string => {
    // Input: 2024-11-20T00:00:00+05:30"
    // Output: 20-NOV-2024
    if (!dateString) {
        return "";
    }

    // Extract the date parts (year, month, day) from the original string
    const [datePart] = dateString.split("T"); // Extract the date portion (ignores time and zone)
    const [year, month, day] = datePart.split("-"); // Split the date part into year, month, and day

    // Create a new Date object using the extracted values (ignores time zone)
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))); // Month is 0-indexed in JavaScript

    // Set the year to 2024 (no time zone change)
    date.setUTCFullYear(2024);

    // Format the components
    const monthName: string = date.toLocaleString("en-US", { month: "short" }); // "Nov"
    const dayStr: string = String(date.getUTCDate()).padStart(2, "0"); // "13"
    const yearStr: number = date.getUTCFullYear(); // "2024"

    // Return the formatted string
    return `${dayStr}-${monthName.split(",")[0].toUpperCase()}-${yearStr}`;
};

const CustomOrderStatusPageReducer = {
    // Get order details
    "Custom/OrderStatus/BeginLoadData": (draft: Draft<OrderStatusModel>) => {
        draft.isLoading = true;
    },
    "Custom/OrderStatus/LoadDataError": (
        draft: Draft<OrderStatusModel>,
        action: {
            message: string;
        },
    ) => {
        draft.isLoading = false;
        draft.error = action.message;
    },
    "Custom/OrderStatus/CompleteLoadData": (
        draft: Draft<OrderStatusModel>,
        action: {
            result: OrderStatusApiResultModel;
            orderId: string;
        },
    ) => {
        draft.orderStatusById[action.orderId] = action.result;
        draft.isLoading = false;
        draft.error = "";
        draft.pickupWindow.pickUpWindowByOrderId[action.orderId] = {
            order: {
                pickUpDate: action.result.properties?.requestedDate,
                pickUpTimeSlot: (action.result.properties?.requestedPickUpTimeSlot?.replace(/\./g, ":") || "").toUpperCase(),
                warehouseId: action.result.properties?.fulfilmentWarehouse || "",
            }
        }
    },

    // Update pickup window details
    "Custom/OrderStatus/PickupWindow/BeginUpdatingData": (draft: Draft<OrderStatusModel>) => {
        draft.pickupWindow.isUpdatingSchedule = true;
    },
    "Custom/OrderStatus/PickupWindow/UpdatingDataError": (
        draft: Draft<OrderStatusModel>,
        action: {
            message: string;
        },
    ) => {
        draft.pickupWindow.isUpdatingSchedule = false;
        draft.pickupWindow.error = action.message;
    },
    "Custom/OrderStatus/PickupWindow/CompleteUpdatingData": (
        draft: Draft<OrderStatusModel>,
        action: {
            result: PickupWindowApiResultModel;
            orderId: string;
        },
    ) => {
        // Updating date format in data
        const data = {...action.result };
        data.order = {
            ...data.order, pickUpDate: data.order?.pickUpDate ? convertToDateString(data.order?.pickUpDate) : ""
        };
        draft.pickupWindow.pickUpWindowByOrderId[action.orderId] = data;
        draft.pickupWindow.isUpdatingSchedule = false;
        draft.pickupWindow.error = "";
    },
};

export default createTypedReducerWithImmer(initialState, CustomOrderStatusPageReducer);
